import React, { useCallback, useMemo } from 'react';

import type { SuggestedEditKind, UUID } from '@aline/common';
import { getStartingPosition, Select } from '@aline/common';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { makeStyles } from 'tss-react/mui';

import { getRuleRunWarning, RULE_RUN_WARNING_MESSAGES } from './rule-run-warning';
import { RuleRow } from './RuleRow';
import type { RuleRowSuggestion } from './RuleRow';
import {
  findStop,
  SuggestionPill,
  useMoveHover,
  useStopDecision,
  useSuggestionReview,
} from './suggested-edits';
import type { PlaybookDetailRule } from './types';

// Shared so a row with no chip keeps one identity across renders.
const EMPTY_RATIONALES: readonly string[] = [];

const useStyles = makeStyles()(theme => ({
  /**
   * The source end of a pending move, inside the row's box under the name and
   * inset a little from the box's edges. The arrow overlay pairs this row with
   * the move's ghost destination.
   */
  moveRow: {
    padding: theme.spacing(0, 0.75, 0.75),
  },
}));

interface SortableRuleRowProps {
  onDelete: (rule: PlaybookDetailRule) => void;
  onSelect: (ruleId: UUID) => void;
  ordinal: number;
  rule: PlaybookDetailRule;
  selected: boolean;
  /** The kind the category card is tinted with as a whole, if any; see `RuleRow`. */
  containerKind?: SuggestedEditKind | null;
  disabled?: boolean;
}

/**
 * A rule row that can be picked up and dropped elsewhere in the list. The
 * sortable id is the rule's own id, which is what the drop handler looks up in
 * the order.
 *
 * Only the listeners go on the grip, not `attributes`: those would make the
 * grip a focusable `role="button"` and announce keyboard pick-up instructions
 * that no keyboard sensor here honours.
 *
 * The row's chip aggregates the rule's suggestions: a rule whose positions
 * carry suggestions reads as edited even if its own fields are untouched. The
 * chip has no ✕ / ✓: each suggestion is decided in the details panel.
 */
export const SortableRuleRow = React.memo(function SortableRuleRow({
  containerKind,
  disabled = false,
  onDelete,
  onSelect,
  ordinal,
  rule,
  selected,
}: SortableRuleRowProps): JSX.Element {
  const { classes } = useStyles();
  const { isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    disabled,
    id: rule.id,
  });
  const { currentStop, model } = useSuggestionReview();
  const kind = model.ruleChipKinds.get(rule.id) ?? null;
  const rationales = model.ruleChipRationales.get(rule.id) ?? EMPTY_RATIONALES;
  const ruleEdit = model.ruleEdits.get(rule.id);
  const moveStop = findStop(model, rule.id, 'move');
  const moveDecision = useStopDecision(moveStop);
  const moveHover = useMoveHover(rule.id);
  const handleDelete = useCallback(() => onDelete(rule), [onDelete, rule]);
  const runWarning = getRuleRunWarning(rule);
  const handleSelect = useCallback(() => onSelect(rule.id), [onSelect, rule.id]);

  const suggestion = useMemo((): RuleRowSuggestion | null => {
    if (!kind) return null;
    const titleDiff =
      ruleEdit?.type === 'change' && ruleEdit.patch?.value.title !== undefined
        ? { after: ruleEdit.patch.value.title, before: rule.title }
        : undefined;
    return { kind, rationales, titleDiff };
  }, [kind, rationales, rule.title, ruleEdit]);

  return (
    <div>
      <RuleRow
        containerKind={containerKind}
        dataCy={Select.playbooks.ruleRow}
        deleteDisabled={disabled}
        dragListeners={listeners}
        dragging={isDragging}
        /**
         * Untitled rules (e.g. ones created before titles existed) fall back to
         * their starting position so the row still says what the rule is.
         */
        fallbackLabel={getStartingPosition(rule.positions)?.ruleText || undefined}
        footer={
          moveStop && (
            <div
              className={classes.moveRow}
              data-cy={Select.playbooks.suggestionMoveStrip}
              data-move-anchor={`${rule.id}:source`}
              onMouseEnter={moveHover.onMouseEnter}
              onMouseLeave={moveHover.onMouseLeave}
            >
              <SuggestionPill
                centered
                acceptDisabledReason={moveStop.blockedReason}
                acceptTitle="Accept move"
                current={currentStop?.key === moveStop.key}
                kind="move"
                label="AI moved"
                rationale={moveStop.rationale}
                rejectTitle="Reject move"
                onAccept={moveDecision.onAccept}
                onReject={moveDecision.onReject}
                onSelect={moveDecision.onSelect}
              />
            </div>
          )
        }
        ordinal={ordinal}
        rootRef={setNodeRef}
        ruleId={rule.id}
        runWarning={runWarning ? RULE_RUN_WARNING_MESSAGES[runWarning] : null}
        selected={selected}
        /**
         * Translate only, like the position and category cards: dnd-kit's scale
         * reports a height ratio that is only ever 1 for these uniform rows.
         */
        style={{ transform: CSS.Translate.toString(transform), transition }}
        suggestion={suggestion}
        title={rule.title}
        onDelete={handleDelete}
        onSelect={handleSelect}
      />
    </div>
  );
});
